/**
 * React hooks for accessing app runtime state
 * 
 * These hooks provide reactive access to query parameters
 * and automatically subscribe to changes.
 */

import { useEffect, useState } from 'react';
import { 
  appRuntime,
  CLUB_ID_PARAM, 
  TENANT_HANDLE_PARAM,
  USER_ID_PARAM 
} from '../appRuntimeService';
import type { QueryParamKey } from '../types';

/**
 * Hook to access and update query parameters from React components
 * 
 * @param key - The parameter key to access
 * @returns [value, setValue] tuple like useState
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [clubId, setClubId] = useQueryParam('club-id');
 *   
 *   return (
 *     <div>
 *       <p>Club ID: {clubId}</p>
 *       <button onClick={() => setClubId('new-id')}>
 *         Change Club
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useQueryParam(key: QueryParamKey): [string | null, (value: string | null) => void] {
  // Initialize state with current value
  const [value, setValue] = useState<string | null>(() => appRuntime.get(key));
  
  useEffect(() => {
    // Warn if not initialized (should never happen if initialized in main.tsx)
    if (!appRuntime.isInitialized) {
      console.warn(
        '[useQueryParam] AppRuntime not initialized. ' +
        'Call initializeAppRuntime() in main.tsx before rendering React.'
      );
    }
    
    // Subscribe to changes
    const unsubscribe = appRuntime.subscribe(key, (newValue) => {
      setValue(newValue);
    });
    
    return unsubscribe;
  }, [key]);
  
  // Return value and setter
  return [
    value,
    (newValue: string | null) => {
      appRuntime.set(key, newValue);
    }
  ];
}

/**
 * Convenience hook for tenant handle parameter
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [tenantHandle] = useTenantHandle();
 *   return <div>Tenant: {tenantHandle}</div>;
 * }
 * ```
 */
export function useTenantHandle(): [string | null, (value: string | null) => void] {
  return useQueryParam(TENANT_HANDLE_PARAM);
}

/**
 * Convenience hook for club ID parameter
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [clubId] = useClubId();
 *   return <div>Club: {clubId}</div>;
 * }
 * ```
 */
export function useClubId(): [string | null, (value: string | null) => void] {
  return useQueryParam(CLUB_ID_PARAM);
}

/**
 * Convenience hook for user ID parameter
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [userId] = useUserId();
 *   return <div>User: {userId}</div>;
 * }
 * ```
 */
export function useUserId(): [string | null, (value: string | null) => void] {
  return useQueryParam(USER_ID_PARAM);
}
